/**
 * 다음에 누를 법한 화면 모듈을 **한가할 때 미리 받아 둔다.**
 *
 * 화면을 쪼갠 뒤로 첫 픽셀은 빨라졌지만(1,123KB → 720KB), 탭을 처음 누를 때마다
 * 그 화면 몫을 새로 받는다. 느린 휴대폰 실측: 준비물 첫 진입 3,177ms → 받아 둔
 * 뒤 1,502ms. **그 차이가 쪼갠 대가다.**
 *
 * 그래서 쪼개기를 되돌리는 대신 미리 받는다. `requestIdleCallback`으로 브라우저가
 * 할 일이 없을 때만 받으므로 지금 보고 있는 화면을 느리게 하지 않는다.
 * 같은 import 지정자라 Vite가 같은 청크로 묶고, 브라우저 캐시에 들어간다.
 *
 * 실패는 무시한다 — 미리 받기가 안 돼도 누를 때 받으면 된다.
 */
function whenIdle(fn: () => void): () => void {
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout: 4000 });
    return () => w.cancelIdleCallback?.(id);
  }
  // 사파리에는 없다. 2초 뒤로 미루는 걸로 갈음한다.
  const id = window.setTimeout(fn, 2000);
  return () => window.clearTimeout(id);
}

/**
 * 여행 안에서 옮겨 다닐 탭들. 지도·차트가 무거워 효과가 크다.
 *
 * **화면 안에서 또 나눠 싣는 것들까지 받아 둔다.** 차트·지도는 화면 모듈이
 * 아니라 그 안에서 `import()`로 불러오는데, 오프라인에서 그 요청이 실패하면
 * 화면이 통째로 "표시하지 못했습니다"가 된다(실측: 비행기 모드에서 비용 탭의
 * 도넛 차트). 미리 받아 두면 캐시에서 나온다.
 */
export function prefetchTripTabs(): () => void {
  return whenIdle(() => {
    void import("./routes/trip-checklist");
    void import("./routes/trip-budget");
    void import("./routes/trip-map");
    void import("./routes/trip-journal");
    void import("./routes/trip-settings");
    // 화면 안에서 늦게 불러오는 것들 — 경로는 src/components 기준이다.
    void import("@/components/budget/expense-donut-chart");
    void import("@/components/budget/comparison-chart");
    void import("@/components/map/trip-map");
  });
}

/**
 * 목록 화면에서 다음에 갈 만한 곳.
 *
 * **일정 화면이 여기 있는 이유**: 예전에는 메인 번들에 박혀 있었다. 빼니
 * 엔트리가 gzip 214KB → 195KB로 줄었는데(느린 회선에서 그만큼 첫 화면이
 * 빨라진다), 그러면 여행을 누를 때 새로 받아야 한다. 목록을 보는 동안
 * 한가할 때 받아 두면 둘 다 얻는다 — 목록에서 여행을 누르는 건 거의
 * 확정된 다음 동작이라 헛수고가 될 일도 적다.
 */
export function prefetchFromTripList(): () => void {
  return whenIdle(() => {
    void import("./routes/trip-itinerary");
    void import("./routes/trip-new");
    void import("./routes/profile");
  });
}
